import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import './gooey.css'

interface GooeyBackgroundProps {
  children?: ReactNode
  /**
   * Keeps the centre of the screen black so colour only bleeds in from the
   * edges. Switch off to see the raw blob field.
   */
  mask?: boolean
  className?: string
}

/**
 * Animated "gooey" colour field.
 *
 * Five blurred radial blobs drift on their own timers and are merged by an SVG
 * filter, so overlapping edges fuse instead of stacking. A sixth blob follows
 * the pointer.
 *
 * Adapted from a reference implementation for this design system:
 *   - the base is pure black rather than a purple/navy gradient
 *   - blending is `screen`, not `hard-light`: on black, hard-light swallows
 *     anything darker than 50% grey, which kills most of the glow
 *   - the palette is the WonderWorks mark's own blue → magenta → orange
 *   - a radial mask keeps the middle black, so colour reads as light leaking
 *     in from behind the screen
 *   - the pointer loop cancels its animation frame on unmount, and never
 *     starts when the visitor asked for reduced motion
 */
export function GooeyBackground({ children, mask = true, className = '' }: GooeyBackgroundProps) {
  const interactiveRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const node = interactiveRef.current
    if (!node) return

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const finePointer = window.matchMedia('(pointer: fine)').matches
    if (reduceMotion || !finePointer) return

    let currentX = window.innerWidth / 2
    let currentY = window.innerHeight / 2
    let targetX = currentX
    let targetY = currentY
    let frame = 0

    const handlePointerMove = (event: PointerEvent) => {
      targetX = event.clientX
      targetY = event.clientY
    }

    const step = () => {
      // Ease towards the pointer so the blob lags behind it slightly.
      currentX += (targetX - currentX) / 20
      currentY += (targetY - currentY) / 20
      node.style.transform = `translate(${Math.round(currentX)}px, ${Math.round(currentY)}px)`
      frame = requestAnimationFrame(step)
    }

    window.addEventListener('pointermove', handlePointerMove)
    frame = requestAnimationFrame(step)

    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      cancelAnimationFrame(frame)
    }
  }, [])

  return (
    <div className={`goo${className ? ` ${className}` : ''}`}>
      <div className="goo__field" aria-hidden="true">
        <svg className="goo__filter" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <filter id="wonderworks-goo">
              <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="blur" />
              <feColorMatrix
                in="blur"
                mode="matrix"
                values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -8"
                result="goo"
              />
              <feBlend in="SourceGraphic" in2="goo" />
            </filter>
          </defs>
        </svg>

        <div className="goo__blobs">
          <div className="goo__blob goo__blob--1" />
          <div className="goo__blob goo__blob--2" />
          <div className="goo__blob goo__blob--3" />
          <div className="goo__blob goo__blob--4" />
          <div className="goo__blob goo__blob--5" />
          <div ref={interactiveRef} className="goo__blob goo__blob--pointer" />
        </div>

        {mask ? <div className="goo__mask" /> : null}
      </div>

      <div className="goo__content">{children}</div>
    </div>
  )
}
